<script setup lang="ts">
    import { CardTitle } from '@/components/ui/card';
    import { columns } from '@/tables/columns/bank';
    import { DatePicker } from '@/components/ui/form';
    import { getLocalTimeZone, today } from '@internationalized/date';
    import { Head } from '@inertiajs/vue3';
    import { ref, computed } from 'vue';
    import { Separator } from '@/components/ui/separator';
    import { useI18n } from 'vue-i18n';
    import { usePage } from '@inertiajs/vue3';
    import { usePermissions } from '@/composables/usePermissions';
    import { useQuery } from '@tanstack/vue-query';
    import AppLayout from '@/layouts/AppLayout.vue';
    import axios from 'axios';
    import ConnectBank from '@/components/bank/ConnectBank.vue';
    import DataTable from '@/components/datatable/DataTable.vue';
    import DataTableToolbarActions from '@/components/datatable/DataTableToolbarActions.vue';
    import DisconnectBank from '@/components/bank/DisconnectBank.vue';
    import HoverInfo from '@/components/HoverInfo.vue';
    import PageCard from '@/components/PageCard.vue';
    import Permission from '@/enums/Permission';
    import QueryKey from '@/enums/QueryKey';
    import type { SharedData, BreadcrumbItem } from '@/types';

    const props = defineProps<{
        link?: string;
        isConnected?: boolean;
    }>();

    const { t } = useI18n();
    const { hasPermission } = usePermissions();
    const page = usePage<SharedData>();
    const selectedCompanyId = computed(() => page.props.auth.user.selected_company_id);
    const perDate = ref(today(getLocalTimeZone()));

    const breadcrumbs: BreadcrumbItem[] = [
        {
            title: 'menu.bank',
            href: route('v2.bank.index'),
        },
    ];

    const { isPending, data } = useQuery({
        queryKey: [QueryKey.BANK, selectedCompanyId, perDate],
        queryFn: getBank,
    });

    async function getBank() {
        const { data } = await axios.get(route('v2.bank.table', selectedCompanyId.value), {
            params: {
                per_date: perDate.value,
            },
        });
        return data.data;
    }
</script>

<template>
    <Head :title="t('viia.header')" />

    <AppLayout
        :breadcrumbs="breadcrumbs"
        require-selected-company
    >
        <PageCard v-if="hasPermission(Permission.BANK_VIEW)">
            <DataTable
                :table-key="QueryKey.BANK"
                :columns="columns"
                :data="data"
                :loading="isPending"
                :default-column-visibility="{
                    id: false,
                    automatic_transfer: false,
                }"
            >
                <template #title>
                    <CardTitle>
                        {{ t('viia.header') }}
                    </CardTitle>
                </template>
                <template #actions>
                    <DataTableToolbarActions name="bank">
                        <span class="flex shrink-0 items-center gap-1 text-[10px] uppercase">
                            {{ t('viia.date_selection') }}
                            <HoverInfo :content="t('viia.dato_help')" />
                        </span>
                        <DatePicker v-model="perDate" />
                        <Separator
                            orientation="vertical"
                            class="hidden h-2.5! md:flex"
                        />
                        <DisconnectBank
                            v-if="isConnected"
                            :company-id="selectedCompanyId"
                        />
                        <ConnectBank
                            v-else-if="link"
                            :link="link"
                        />
                    </DataTableToolbarActions>
                </template>
            </DataTable>
        </PageCard>
    </AppLayout>
</template>
